import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormHeading, FormHelperText, FormNumericInput, FormSelect, FormSwitch, FormTextInput } from 'components/form-fields';
import { Table } from 'components/listing';
import { StatusBlock } from 'components/status';
import { ShowHide } from 'components/layout';
import { defaultSignOffAlertDelayUnitId, hasEmployeeDrivenSignOff, maxSignOffAlertDelayValue, signOffMethodDescriptions } from 'pages/employee-reviews/review-types/shared/reviewTypeEditorHelpers';
import './ReviewTypeEditor.css';

const defaultObject = {};

const signOffMethodTableHeaders = {
    headers: [
        { key: 'TYPE', value: 'Type' },
        { key: 'DESCRIPTION', value: 'Description' },
        { key: 'METHODS', value: '' }
    ]
};

const levelTableHeaders = {
    headers: [
        { key: 'LEVEL', value: 'Level' },
        { key: 'LEVELNAME', value: 'Level Name' }
    ]
};

// Narrow badge column so the level-name input takes the remaining width.
const levelTableColumnWidths = [100];

export function ReviewTypeSignOffStep(
    {
        reviewType,
        onUpdate,
        errors = defaultObject,
        reviewOptions = defaultObject,
        loading = false,
        saving = false
    }
) {

    function onRequireManagerSignatureChange(event) {
        onUpdate({
            ...reviewType,
            requireManagerSignature: event.target.checked
        });
    }

    function onSignOffMethodChange(methodId, event) {
        let newMethodIds;

        if (event.currentTarget.checked) {
            newMethodIds = [...reviewType.signOffMethodIds, methodId];
        } else {
            newMethodIds = reviewType.signOffMethodIds.filter((id) => id !== methodId);
        }

        const updatedReviewType = {
            ...reviewType,
            signOffMethodIds: newMethodIds
        };

        // Once no method leaves the signature with the employee, nothing can be left
        // outstanding and the sign-off alert setting disappears - so it and its wait are
        // cleared rather than left holding values the user can no longer see.
        if (!hasEmployeeDrivenSignOff(updatedReviewType, reviewOptions)) {
            updatedReviewType.raiseAlertForSignOff = false;
            updatedReviewType.signOffAlertDelayValue = 0;
            updatedReviewType.signOffAlertDelayUnitId = defaultSignOffAlertDelayUnitId;
        }

        onUpdate(updatedReviewType);
    }

    function onRaiseAlertForSignOffChange(event) {
        const raiseAlert = event.target.checked;

        onUpdate({
            ...reviewType,
            raiseAlertForSignOff: raiseAlert,
            // The wait is only shown while the alert is on, so both halves of it are
            // cleared with the alert.
            signOffAlertDelayValue: raiseAlert ? reviewType.signOffAlertDelayValue : 0,
            signOffAlertDelayUnitId: raiseAlert ? reviewType.signOffAlertDelayUnitId : defaultSignOffAlertDelayUnitId
        });
    }

    function onSignOffAlertDelayValueChange(value) {
        onUpdate({
            ...reviewType,
            signOffAlertDelayValue: value
        });
    }

    function onSignOffAlertDelayUnitChange(item) {
        onUpdate({
            ...reviewType,
            signOffAlertDelayUnitId: item.id
        });
    }

    function onOutcomeLevelsActiveChange(event) {
        const active = event.target.checked;

        onUpdate({
            ...reviewType,
            outcomeLevelsActive: active,
            // Mandatory levels cannot apply when levels are disabled, so switching
            // levels off clears the mandatory flag.
            outcomeLevelsMandatory: active ? reviewType.outcomeLevelsMandatory : false
        });
    }

    function onOutcomeLevelsMandatoryChange(event) {
        onUpdate({
            ...reviewType,
            outcomeLevelsMandatory: event.target.checked
        });
    }

    function onLevelNameChange(levelNumber, event) {
        onUpdate({
            ...reviewType,
            levels: reviewType.levels.map((level) => {
                if (level.levelNumber !== levelNumber) {
                    return level;
                }

                return {
                    ...level,
                    levelName: event.target.value
                };
            })
        });
    }

    function getLevelColour(levelNumber) {
        const option = (reviewOptions?.outcomeLevels ?? []).find((level) => level.number === levelNumber);

        if (option?.colour == null) {
            return "";
        }

        // StatusBlock expects the hex value without the leading hash.
        return option.colour.replace("#", "");
    }

    function buildSignOffMethodRows() {
        return (reviewOptions?.signOffMethods ?? []).map((method) => {
            return {
                cells: [
                    method.name,
                    signOffMethodDescriptions[method.key],
                    <FormSwitch
                        key={`sign-off-method-${method.key}`}
                        id={`sign-off-method-switch-${method.id}`}
                        onChange={(event) => onSignOffMethodChange(method.id, event)}
                        checked={reviewType?.signOffMethodIds?.some((id) => id === method.id)}
                        loading={loading}
                        disabled={saving}
                    />
                ],
                key: method.key
            };
        });
    }

    function buildLevelRows() {
        return (reviewType?.levels ?? []).map((level) => {
            return {
                cells: [
                    <span key={`review-type-level-status-${level.levelNumber}`} className="review-type-level-status"><StatusBlock background={getLevelColour(level.levelNumber)}>{level.levelNumber}</StatusBlock></span>,
                    <FormTextInput
                        key={`review-type-level-input-${level.levelNumber}`}
                        id={`review-type-level-${level.levelNumber}`}
                        value={level.levelName}
                        maxLength={255}
                        fill
                        loading={loading}
                        disabled={saving}
                        onChange={(event) => onLevelNameChange(level.levelNumber, event)}
                    />
                ],
                key: level.levelNumber
            };
        });
    }

    return (
        <div>
            <h2 className="spacer-bottom">Sign-off &amp; Outcome levels</h2>
            <h3>Sign-off</h3>
            <FormHelperText loading={loading}>Choose how this review is signed off when it&apos;s completed.</FormHelperText>
            <FormSwitch
                id="review-type-manager-signature"
                headingText="Require manager signature"
                checked={reviewType?.requireManagerSignature}
                onChange={onRequireManagerSignatureChange}
                loading={loading}
                disabled={saving}
            />
            <div className="spacer-bottom">
                <FormHeading loading={loading} headingLevel={"h4"}>Employee Sign-off methods</FormHeading>
                <Table
                    headerData={signOffMethodTableHeaders}
                    data={buildSignOffMethodRows()}
                    loadingData={loading}
                    fitToContent={false}
                    dangerHelperText={errors.signOffMethodIds}
                />
            </div>
            {/* Only the Email and App methods hand the signature to the employee, so
                only they can leave a completed review awaiting one. */}
            <div className="spacer-bottom">
                <ShowHide
                    evaluator={hasEmployeeDrivenSignOff(reviewType, reviewOptions)}
                    show={(
                        <Fragment>
                            <FormSwitch
                                id="review-type-raise-alert-for-sign-off"
                                headingText="Raise an alert for outstanding sign-off"
                                checked={reviewType?.raiseAlertForSignOff}
                                onChange={onRaiseAlertForSignOffChange}
                                helperText="The alert is raised while a completed review is still waiting for the employee's signature."
                                loading={loading}
                                disabled={saving}
                            />
                            <ShowHide
                                evaluator={reviewType?.raiseAlertForSignOff === true}
                                show={(
                                    <div className="form-field">
                                        <FormHeading loading={loading} headingLevel={"h4"}>Sign-off alert delay</FormHeading>
                                        <FormHelperText loading={loading}>How long a completed review is left awaiting the signature before the alert is raised. Zero raises it as soon as the review is completed.</FormHelperText>
                                        <div className="inline-items">
                                            <div>Wait</div>
                                            <FormNumericInput
                                                id="review-type-sign-off-alert-delay-value"
                                                min={0}
                                                max={maxSignOffAlertDelayValue}
                                                loading={loading}
                                                disabled={saving}
                                                selectedValue={reviewType?.signOffAlertDelayValue}
                                                onValueChange={onSignOffAlertDelayValueChange}
                                            />
                                            <FormSelect
                                                id="review-type-sign-off-alert-delay-unit"
                                                items={reviewOptions?.advanceNoticeUnits}
                                                loading={loading}
                                                disabled={saving}
                                                placeholder="Choose a unit"
                                                selectedValue={reviewType?.signOffAlertDelayUnitId}
                                                onItemSelect={onSignOffAlertDelayUnitChange}
                                                small
                                            />
                                            <div>after the review is completed</div>
                                        </div>
                                        {/* One message covers the whole row, and it sits outside the inline
                                            row so the row's vertical alignment doesn't shift while showing. */}
                                        <FormHelperText loading={loading} danger={true}>{errors.signOffAlertDelayValue ?? errors.signOffAlertDelayUnitId}</FormHelperText>
                                    </div>
                                )}
                            />
                        </Fragment>
                    )}
                />
            </div>
            <h3>Outcome levels</h3>
            <FormHelperText loading={loading}>Record an outcome level against each completed review.</FormHelperText>
            <FormSwitch
                id="review-type-outcome-levels-active"
                headingText="Enable outcome levels"
                checked={reviewType?.outcomeLevelsActive}
                onChange={onOutcomeLevelsActiveChange}
                loading={loading}
                disabled={saving}
            />
            <ShowHide
                evaluator={reviewType?.outcomeLevelsActive === true}
                show={(
                    <Fragment>
                        <FormSwitch
                            id="review-type-outcome-levels-mandatory"
                            headingText="Make levels mandatory"
                            checked={reviewType?.outcomeLevelsMandatory}
                            onChange={onOutcomeLevelsMandatoryChange}
                            helperText="If enabled, a level must be selected when completing a review."
                            loading={loading}
                            disabled={saving}
                        />
                        <div className="spacer-bottom">
                            <FormHeading loading={loading} headingLevel={"h4"}>Configure level names</FormHeading>
                            <Table
                                headerData={levelTableHeaders}
                                data={buildLevelRows()}
                                loadingData={loading}
                                fitToContent={false}
                                columnWidths={levelTableColumnWidths}
                            />
                            <ShowHide
                                evaluator={errors.levels != null}
                                show={(
                                    <div className="form-field">
                                        <FormHelperText loading={loading} danger={true}>{errors.levels}</FormHelperText>
                                    </div>
                                )}
                            />
                        </div>
                    </Fragment>
                )}
            />
        </div>
    );
}

ReviewTypeSignOffStep.propTypes = {
    reviewType: PropTypes.object.isRequired,
    onUpdate: PropTypes.func.isRequired,
    errors: PropTypes.object,
    reviewOptions: PropTypes.object,
    loading: PropTypes.bool,
    saving: PropTypes.bool
};
